import { getDateRangeForOption, RangeOptionType, SpanType } from '@enter/core';
import * as d3 from 'd3';
import _ from 'lodash';
import { action, observable } from 'mobx';
import { Granularity } from 'models';

export type DateRangeType = 'service' | 'billing';

export class DateRangeStore {
    @observable public loading: boolean = true;
    @observable public dateRange: [Date, Date];
    @observable public dateRangeOption: RangeOptionType;
    @observable public dateRangeType: DateRangeType;

    constructor() {
        const savedDateRangeAndOption = window.localStorage.getItem('date_range');
        if (savedDateRangeAndOption) {
            const dateRangeAndOption = JSON.parse(savedDateRangeAndOption);
            this.dateRangeOption = dateRangeAndOption[1];
            // NB: when restoring, use DateRangeOption to rebuild date range UNLESS option is 'custom'
            // otherwise, end of range may not match current date
            this.dateRange =
                this.dateRangeOption === 'custom'
                    ? dateRangeAndOption[0].map((d: string) => new Date(d))
                    : getDateRangeForOption(this.dateRangeOption);
            this.dateRangeType = dateRangeAndOption[2] as DateRangeType;
        } else {
            this.dateRangeType = 'service';
            this.dateRangeOption = '180days';
            this.dateRange = getDateRangeForOption(this.dateRangeOption);
        }
    }

    @action
    public setDateRange(dateRange: [Date, Date]): void {
        this.dateRange = dateRange;
        this.saveDateRange();
    }

    @action
    public setDateRangeOption(option: RangeOptionType): void {
        this.dateRangeOption = option;
        this.saveDateRange();
    }

    @action
    public setDateRangeType(type: DateRangeType): void {
        this.dateRangeType = type;
        this.saveDateRange();
    }

    // FIXME extract to util
    public countPeriodsInDateRange(granularity: Granularity): number {
        const [startDate, endDate] = this.dateRange;
        switch (granularity) {
            case Granularity.Daily:
                return d3.utcDay.count(startDate, endDate);
            case Granularity.Weekly:
                return d3.utcWeek.count(startDate, endDate);
            case Granularity.Monthly:
            default:
                return d3.utcMonth.count(startDate, endDate);
        }
    }
    public spanTypeForGranularity(granularity: Granularity): SpanType {
        switch (granularity) {
            case Granularity.Daily:
                return SpanType.DAYOFYEAR;
            case Granularity.Weekly:
                return SpanType.WEEKOFYEAR;
            case Granularity.Monthly:
                return SpanType.MONTH;
            default:
                throw new Error(`Unknown granularity type: ${granularity}`);
        }
    }
    public hasGranularityForDateRange(granularity: Granularity): boolean {
        return this.countPeriodsInDateRange(granularity) > 1;
    }
    public findGranularityForDateRange(): Granularity {
        // ordered in reverse-size order, so find first with enough periods
        const ordered = [Granularity.Monthly, Granularity.Weekly, Granularity.Daily];
        const periods = _.map(ordered, g => this.countPeriodsInDateRange(g));
        const i = _.findIndex(periods, n => n > 1);
        return _.get(ordered, i, Granularity.Daily);
    }

    private readonly saveDateRange = (): void => {
        window.localStorage.setItem(
            'date_range',
            JSON.stringify([this.dateRange, this.dateRangeOption, this.dateRangeType]),
        );
    };
}
